import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { HTTPCONSTANTS } from '../http/http-constants';
import { LoginAuthenticationResponse } from '../models/loginAuthenticationResponse';
import { RegisterAuthenticationResponse } from '../models/registerAuthenticationResponse';
import { LoginModel } from '../models/loginModel';
import { RegisterModel } from '../models/registerModel';
import { GoogleLoginModel } from '../models/googleLoginModel';
import { FacebookLoginModel } from '../models/facebookLoginModel';
import { AppleLoginModel } from '../models/appleLoginModel';
import { tap } from 'rxjs/operators';
import { uuid4 } from '@sentry/utils';

export enum UserType {
  Admin = 0,
  Standard = 1,
  Dealer = 2,
}

export enum SessionState {
  LOGGEDIN = 0,
  EXPIRED = 1,
  EMPTY = 2,
}

export class Session {
  public state: SessionState = SessionState.EMPTY;
  public token: string;
  public expiry: Date;
  public type: number;
}

class QueudRequest {
  constructor(public url: string, public callback: (data: any) => void) { }
}

@Injectable({ providedIn: 'root' })
export class SessionService {
  private currentSession: Session;
  private localSession: string;

  private requestQueue: QueudRequest[] = [];
  public requestingAccess: boolean = false;
  public sessionStateChange: EventEmitter<void> = new EventEmitter();

  constructor(
    private http: HttpClient
  ) { }

  public Start(): void {
    this.IdentifyUser();
    this.LogAdMetrics();
  }

  private LogAdMetrics(): void {
    var trackingParams = new URLSearchParams(window.location.search);
    for (const [key, value] of trackingParams.entries()) {
      if (key === 'plxad') {
        console.log('trigger platex ad event');
        window.gtag('event', 'platex_ad_trigger', {
          advert_id: value,
        });
      }
    }

  }

  private IdentifyUser(): void {
    if (this.isLoggedIn()) {
      this.http
        .get<any>(
          `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/authenticate/identify`
        )
        .subscribe(() => { });
    } else {
      var localSessionId = localStorage.getItem('local_session');
      if (localSessionId == null) {
        localSessionId = uuid4();
        localStorage.setItem('local_session', localSessionId);
      }
    }
  }

  public setSuccessRoute(route: string) {
    localStorage.setItem('loginSuccessRoute', route);
  }

  public getSuccessRoute(): string {
    const successRoute = localStorage.getItem('loginSuccessRoute');
    localStorage.removeItem('loginSuccessRoute');
    if (successRoute == null) return '/';
    if (successRoute == '/login') return '/';
    if (successRoute == '/register') return '/';
    return successRoute;
  }

  public isLoggedIn(): boolean {
    this.validState();
    return this.currentSession.state == SessionState.LOGGEDIN;
  }

  public ClearQueue(): void {
    this.requestQueue = [];
  }

  public AddToQueue(url: string, callback: (data: any) => void): void {
    var queueItem = new QueudRequest(url, callback);
    this.requestQueue.push(queueItem);
  }

  public EnterQueue(callback: () => void): void {
    var request: QueudRequest = this.requestQueue.pop();
    this.RemakeRequestAfterAuth(request, callback);
  }

  private RemakeRequestAfterAuth(
    queueItem: QueudRequest,
    callback: () => void
  ): void {
    if (queueItem == undefined || queueItem == null) {
      this.QueueCleared(callback);
      return;
    }

    this.http.get(queueItem.url).subscribe((_: any) => {
      queueItem.callback(_);

      _ = this.requestQueue.pop();
      if (_ != undefined) this.RemakeRequestAfterAuth(_, callback);
      else this.QueueCleared(callback);
    });
  }

  private QueueCleared(callback: () => void): void {
    this.sessionStateChange.emit();
    callback();
  }

  // ONLY USED LOCALLY, API HANDLES AUTH
  public isAdmin(): boolean {
    if (!this.isLoggedIn()) return false;
    return this.currentSession.type == UserType.Admin;
  }

  public isDealer(): boolean {
    if (!this.isLoggedIn()) return false;
    return this.currentSession.type == UserType.Dealer;
  }

  public invalidateSession(): void {
    this.validState();
    this.currentSession.state = SessionState.EXPIRED;
    this.saveSession(this.currentSession);
  }

  public RefreshToken(): Observable<any> {
    return this.makeRefreshRequest().pipe(
      tap((resp: LoginAuthenticationResponse) => {
        var session = this.createNewSession(resp);
        this.saveSession(session);
      })
    );
  }

  // POST /authenticate/forgot-password — always 200 from the server regardless
  // of whether the email exists. UI must NOT branch on success vs error in a
  // way that would leak account existence.
  public requestPasswordReset(email: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/authenticate/forgot-password`,
      { email }
    );
  }

  // PUT /authenticate/reset — consumes a single-use Identity reset token.
  // Token + email come from the reset-link query string.
  public resetPassword(
    email: string,
    token: string,
    newPassword: string
  ): Observable<any> {
    return this.http.put(
      `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/authenticate/reset`,
      { email, token, newPassword }
    );
  }

  public login(
    loginModel: LoginModel,
    loginCallback: (resp: boolean, message?: string) => void
  ): void {
    this.makeLoginRequest(loginModel).subscribe(
      (resp: LoginAuthenticationResponse) => {
        loginCallback(true);
        var session = this.createNewSession(resp);
        this.IdentifyUser();
        this.saveSession(session);
      },
      (error: HttpErrorResponse) => {
        switch (error.status) {
          case 401:
            if (error.error.toLowerCase().startsWith('invalid credentials')) {
              loginCallback(false, HTTPCONSTANTS.incorrectLogin);
            } else {
              loginCallback(false, error.error);
            }
            break;

          default:
            loginCallback(false, HTTPCONSTANTS.serverError);
            break;
        }
      }
    );
  }

  public handleGoogleLogin(
    idToken: string,
    signupReason: string,
    loginCallback: (resp: boolean, message?: string) => void
  ): void {
    // Debug: Log token info (first part only for security)
    console.log('Google ID Token received:', idToken.substring(0, 50) + '...');
    
    // Decode token to verify structure (for debugging)
    try {
      const tokenParts = idToken.split('.');
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        console.log('Token payload:', {
          iss: payload.iss,
          aud: payload.aud,
          sub: payload.sub,
          email: payload.email,
          exp: payload.exp,
          iat: payload.iat
        });
        
        // Verify audience matches client ID
        const expectedAud = '505978889074-sj1une8r7955ofq2uasjq11g0eia8p6r.apps.googleusercontent.com';
        if (payload.aud !== expectedAud) {
          console.error('Token audience mismatch!', {
            expected: expectedAud,
            received: payload.aud
          });
        }
      }
    } catch (e) {
      console.error('Error decoding token:', e);
    }

    // Send the ID token to your backend
    const googleLoginModel = new GoogleLoginModel();
    googleLoginModel.idToken = idToken;
    googleLoginModel.signupReason = signupReason;

    console.log('Sending Google login request:', {
      hasToken: !!googleLoginModel.idToken,
      tokenLength: googleLoginModel.idToken?.length,
      signupReason: googleLoginModel.signupReason
    });

    this.makeGoogleLoginRequest(googleLoginModel).subscribe(
      (resp: LoginAuthenticationResponse) => {
        loginCallback(true);
        var session = this.createNewSession(resp);
        this.IdentifyUser();
        this.saveSession(session);
      },
      (error: HttpErrorResponse) => {
        console.error('Google login error:', {
          status: error.status,
          statusText: error.statusText,
          error: error.error,
          headers: error.headers
        });
        
        switch (error.status) {
          case 401:
            const errorMessage = error.error || 'Google sign-in failed. Please try again.';
            console.error('Backend rejected token:', errorMessage);
            loginCallback(false, errorMessage);
            break;
          default:
            loginCallback(false, HTTPCONSTANTS.serverError);
            break;
        }
      }
    );
  }

  public handleFacebookLogin(
    accessToken: string,
    signupReason: string,
    loginCallback: (resp: boolean, message?: string) => void
  ): void {
    console.log('Facebook access token received:', accessToken.substring(0, 50) + '...');

    // Send the access token to your backend
    const facebookLoginModel = new FacebookLoginModel();
    facebookLoginModel.accessToken = accessToken;
    facebookLoginModel.signupReason = signupReason;

    console.log('Sending Facebook login request:', {
      hasToken: !!facebookLoginModel.accessToken,
      tokenLength: facebookLoginModel.accessToken?.length,
      signupReason: facebookLoginModel.signupReason
    });

    this.makeFacebookLoginRequest(facebookLoginModel).subscribe(
      (resp: LoginAuthenticationResponse) => {
        loginCallback(true);
        var session = this.createNewSession(resp);
        this.IdentifyUser();
        this.saveSession(session);
      },
      (error: HttpErrorResponse) => {
        console.error('Facebook login error:', {
          status: error.status,
          statusText: error.statusText,
          error: error.error,
          headers: error.headers
        });
        
        switch (error.status) {
          case 401:
            const errorMessage = error.error || 'Facebook sign-in failed. Please try again.';
            console.error('Backend rejected token:', errorMessage);
            loginCallback(false, errorMessage);
            break;
          default:
            loginCallback(false, HTTPCONSTANTS.serverError);
            break;
        }
      }
    );
  }

  public handleAppleLogin(
    code: string,
    signupReason: string,
    loginCallback: (resp: boolean, message?: string) => void
  ): void {
    debugger;
    const appleLoginModel = new AppleLoginModel();
    appleLoginModel.code = code;
    appleLoginModel.signupReason = signupReason;

    this.makeAppleLoginRequest(appleLoginModel).subscribe(
      (resp: LoginAuthenticationResponse) => {
        loginCallback(true);
        var session = this.createNewSession(resp);
        this.IdentifyUser();
        this.saveSession(session);
      },
      (error: HttpErrorResponse) => {
        switch (error.status) {
          case 401:
            const errorMessage = error.error || 'Apple sign-in failed. Please try again.';
            loginCallback(false, errorMessage);
            break;
          default:
            loginCallback(false, HTTPCONSTANTS.serverError);
            break;
        }
      }
    );
  }

  public register(
    registerModel: RegisterModel,
    registerCallback: (
      resp: boolean,
      message?: string,
      errors?: string[]
    ) => void
  ): void {
    this.makeRegisterRequest(registerModel).subscribe(
      (resp: RegisterAuthenticationResponse) => {
        registerCallback(true);
        this.IdentifyUser();
      },
      (httpError: HttpErrorResponse) => {
        const error: RegisterAuthenticationResponse = httpError.error;
        registerCallback(false, error.message, error.errors);
      }
    );
  }

  public signOut(callback: () => void): void {
    localStorage.removeItem('pla-session');
    callback();
  }

  public registerToLogin(registerModel: RegisterModel): LoginModel {
    const loginModel = new LoginModel();
    loginModel.email = registerModel.email;
    loginModel.password = registerModel.password;
    return loginModel;
  }

  public getToken(): string {
    return this.isLoggedIn() ? this.currentSession.token : '';
  }

  private validState(): void {
    this.getCurrentSession();
  }

  private getCurrentSession(): void {
    this.currentSession = this.retrieveSession();
  }

  private createNewSession(
    loginResponse: LoginAuthenticationResponse
  ): Session {
    var newSession = new Session();
    newSession.token = loginResponse.token;
    newSession.expiry = new Date(loginResponse.expiration);
    newSession.state = SessionState.LOGGEDIN;
    newSession.type = loginResponse.type;
    return newSession;
  }

  private saveSession(session: Session): void {
    var string_session = JSON.stringify(session);
    localStorage.setItem('pla-session', string_session);
    this.currentSession = session;
  }

  private retrieveSession(): Session {
    var string_session = localStorage.getItem('pla-session');
    if (string_session != null) {
      var session = JSON.parse(string_session) as Session;
      return session;
    }
    return new Session();
  }

  // Http

  private makeLoginRequest(
    loginModel: LoginModel
  ): Observable<LoginAuthenticationResponse> {
    return this.http.post<LoginAuthenticationResponse>(
      `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/authenticate/login`,
      loginModel
    );
  }

  private makeRegisterRequest(
    registerModel: RegisterModel
  ): Observable<RegisterAuthenticationResponse> {
    return this.http.post<RegisterAuthenticationResponse>(
      `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/authenticate/register`,
      registerModel
    );
  }

  private makeRefreshRequest(): Observable<LoginAuthenticationResponse> {
    return this.http.post<LoginAuthenticationResponse>(
      `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/authenticate/refresh`,
      {},
      { withCredentials: true }
    );
  }

  private makeGoogleLoginRequest(
    googleLoginModel: GoogleLoginModel
  ): Observable<LoginAuthenticationResponse> {
    return this.http.post<LoginAuthenticationResponse>(
      `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/authenticate/google-login`,
      googleLoginModel
    );
  }

  private makeFacebookLoginRequest(
    facebookLoginModel: FacebookLoginModel
  ): Observable<LoginAuthenticationResponse> {
    return this.http.post<LoginAuthenticationResponse>(
      `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/authenticate/facebook-login`,
      facebookLoginModel
    );
  }

  private makeAppleLoginRequest(
    appleLoginModel: AppleLoginModel
  ): Observable<LoginAuthenticationResponse> {
    return this.http.post<LoginAuthenticationResponse>(
      `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/authenticate/apple-login`,
      appleLoginModel
    );
  }
}
